import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import PastAppointmentRow from "./PastAppointmentRow";
import AppointmentDetailView from "../scheduled/appointmentDetailView";

export default function PastView({ appointments = [], loading = false, error = null, onRefresh }) {
  const [sortConfig, setSortConfig] = useState({ key: "requestedTimestamp", direction: "desc" });
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const formatTime = (timeString) => timeString;
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const sortedAppointments = useMemo(() => {
    const sorted = [...appointments];
    sorted.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case "clientName":
          aValue = `${a.client?.first_name || ""} ${a.client?.last_name || ""}`;
          bValue = `${b.client?.first_name || ""} ${b.client?.last_name || ""}`;
          break;
        case "requestedTimestamp":
          aValue = new Date(a.requestedTimestamp);
          bValue = new Date(b.requestedTimestamp);
          break;
        case "appointmentType":
          aValue = a.appointmentType?.appointment_name || "";
          bValue = b.appointmentType?.appointment_name || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [appointments, sortConfig]);

  const SortableTableCell = ({ column, children, sx, ...props }) => (
    <TableCell
      {...props}
      onClick={() => handleSort(column)}
      sx={{
        cursor: "pointer",
        "&:hover": { backgroundColor: "grey.50" },
        fontFamily: "Poppins",
        fontSize: "16px",
        fontWeight: 500,
        color: "#333333",
        textTransform: "uppercase",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>{children}</Box>
    </TableCell>
  );

  if (selectedAppointment) {
    return (
      <AppointmentDetailView
        appointment={selectedAppointment}
        appointments={sortedAppointments}
        onBack={() => setSelectedAppointment(null)}
        isPast={true}
      />
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography color="error" variant="h6" gutterBottom>
          Error loading past appointments
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={onRefresh} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (sortedAppointments.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h5" gutterBottom>
          No past appointments yet
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: "500px", mx: "auto", mb: 3 }}>
          Once you complete a consultation, it will appear here for your records. You can still chat with clients and review details.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <SortableTableCell column="clientName">Client Name</SortableTableCell>
              <SortableTableCell column="created_at">Submitted updated</SortableTableCell>
              <SortableTableCell column="requestedTimestamp">Appointment Time</SortableTableCell>
              <TableCell>Consultation Type</TableCell>
              <TableCell>Files</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAppointments.map((appointment) => (
              <PastAppointmentRow
                key={appointment.id}
                appointment={appointment}
                onViewDetail={setSelectedAppointment}
                formatDate={formatDate}
                formatTime={formatTime}
                getTimeAgo={getTimeAgo}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
